/* eslint-disable @typescript-eslint/no-explicit-any */
import { Close } from "@mui/icons-material";
import { Chip, useTheme } from "@mui/material";
import * as pimoAppBuilder from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import React, { useMemo, useState } from "react";

import SearchBarV2 from "../../pimo-components/search-bar-v2/search-bar-v2";
import { Column } from "./column";
import { DragDropLayoutComponentLayoutProps } from "./types";

export const Library: React.FC<{
  components: pimoAppBuilder.Component<
    any,
    any,
    any,
    any,
    DragDropLayoutComponentLayoutProps
  >[];
  language?: string;
  onClose?: () => void;
}> = ({ components, language = "en", onClose }) => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const filteredComponents = useMemo(() => {
    if (!normalizedSearchValue) {
      return components;
    }

    return components.filter((component) => {
      const title = component.layoutProps?.title ?? "";
      const description = component.layoutProps?.description ?? "";

      return `${title} ${description}`
        .toLowerCase()
        .includes(normalizedSearchValue);
    });
  }, [components, normalizedSearchValue]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          padding: 0,
        }}
      >
        <h1 style={{ fontSize: "24px", fontWeight: 500 }}>
          {getLocalizedMessage(language, "library.componentLibrary") ||
            "Component Library"}
        </h1>
        <Close onClick={onClose} sx={{ cursor: "pointer" }} />
      </div>

      <p style={{ fontSize: "15px" }}>
        {getLocalizedMessage(language, "library.dragDropInstruction") ||
          "Drag and drop components from the library to the layout."}
      </p>
      <div
        data-pimo-dnd-library-search
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: theme.palette.background.default,
          flexShrink: 0,
          padding: "0 0 16px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
            color: theme.palette.text.primary,
            padding: "4px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <SearchBarV2 onChange={setSearchValue} />
        </div>
      </div>
      <div
        data-pimo-dnd-library-scroll
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            borderBottom: "#DFEFF2 2px solid",
            width: "100%",
            margin: "35px 0",
          }}
        />
        {components.length === 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              fontSize: "18px",
            }}
          >
            {getLocalizedMessage(language, "library.allComponentsUsed") ||
              "All components are already used ✨."}
          </div>
        )}
        {components.length > 0 && filteredComponents.length === 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              fontSize: "18px",
            }}
          >
            No components match your search.
          </div>
        )}
        {filteredComponents.map((c) => {
          return c ? (
            <React.Fragment key={c.layoutProps?.componentId}>
              <Title title={c.layoutProps?.title ?? ""} />
              <ComponentWrapper>
                <Column
                  isEditing={true}
                  onEditComponent={() => {}}
                  component={c}
                  onComponentDropped={() => {}}
                  canDelete={false}
                  onComponentDeleted={() => {}}
                  showMinimized
                />
              </ComponentWrapper>
              <Description description={c.layoutProps?.description ?? ""} />
              <Labels labels={c.layoutProps?.labels ?? []} />
              <div
                style={{
                  borderBottom: "#DFEFF2 2px solid",
                  width: "100%",
                  margin: "35px 0",
                }}
              />
            </React.Fragment>
          ) : (
            <></>
          );
        })}
      </div>
    </div>
  );
};

const ComponentWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div
      style={{
        width: "100%",
        border: "#d8d8d8 1px solid",
        borderRadius: "5px",
      }}
    >
      {children}
    </div>
  );
};

const Title: React.FC<{ title: string }> = ({ title }) => {
  return (
    <div
      style={{
        width: "100%",
        textAlign: "center",
        fontSize: "14px",
        fontWeight: 500,
        marginBottom: "12px",
      }}
    >
      {title}
    </div>
  );
};

const Description: React.FC<{ description: string }> = ({ description }) => {
  return (
    <div
      style={{
        width: "100%",
        fontSize: "12px",
        marginTop: "24px",
      }}
    >
      {description}
    </div>
  );
};

const Labels: React.FC<{ labels: string[] }> = ({ labels }) => {
  return (
    <div
      style={{
        width: "100%",
        marginTop: "24px",
        display: "flex",
        gap: "10px",
      }}
    >
      {labels.map((label) => (
        <Chip
          size="small"
          label={label}
          key={label}
          style={{ color: "#999" }}
        ></Chip>
      ))}
    </div>
  );
};
